import { action, observable } from 'mobx';
import { SortingRule } from 'react-table';
import debounce from 'debounce';

import { getRole, Roles } from 'helpers/roles';
import { mapSortingRulesToValues } from './helpers';

import httpFacade from 'http/httpFacade';

import AppRouter from 'stores/AppRouter';

import { Employee } from './types';

import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  DEFAULT_THROTTLE_MS,
} from '../constants';
import { AccountantEmployeesRequestParams } from 'http/Api/employees';
import Log from 'helpers/log';
import RootStore from 'stores/RootStore';
import { showDate } from 'helpers/datetime';
import { downloadBlob, getFilenameFromContentDisposition } from 'helpers/blob';
import EmployeesForm from 'stores/Forms/EmployeesAccountantForm';

class EmployeesStore {
  @observable data: Employee[] = [];
  @observable sorted: SortingRule[];
  @observable pageCount: number;
  @observable size = DEFAULT_PAGE_SIZE;
  @observable page = DEFAULT_PAGE;
  @observable searchTerm = '';
  @observable form = new EmployeesForm();

  @observable loading = true;

  fetch = getRole(Roles.accountant)
    ? httpFacade.employees.fetchAccountantEmployees
    : httpFacade.employees.fetchHREmployees;

  onSearch = debounce<() => void>(this.fetchEmployees, DEFAULT_THROTTLE_MS);

  @action.bound
  async fetchEmployees(params?: AccountantEmployeesRequestParams) {
    this.loading = true;

    try {
      const { data } = await this.fetch({
        ...(this.sorted !== undefined
          ? { sort: mapSortingRulesToValues(this.sorted) }
          : {}),
        ...(this.size !== undefined ? { size: this.size } : {}),
        ...(this.page !== undefined ? { page: this.page } : {}),
        ...(this.searchTerm ? { q: this.searchTerm } : {}),
        userGroupId: this.form.userGroupId || undefined,
        ...params,
      });
      const { content, totalPages } = data;

      // tie on numberOfElements if necessary, but not on size
      this.data = content;
      this.pageCount = totalPages;
    } catch (error) {
      AppRouter.goBack();
    }

    this.loading = false;
  }

  @action.bound
  async getAccountantExcelReport() {
    try {
      const params = this.form.reportRequestParams;

      const fileName = RootStore.localization.formatMessage(
        'filename.report.excel',
        {
          range:
            params.from !== params.to
              ? `${showDate(params.from)} - ${showDate(params.to)}`
              : showDate(params.from),
        },
      );

      const {
        data,
        headers,
      } = await httpFacade.employees.getAccountantExcelReport(params);
      const blobURL = URL.createObjectURL(data);

      downloadBlob(
        getFilenameFromContentDisposition(headers) || fileName,
        blobURL,
      );
    } catch (error) {
      Log.info(error);
    }
  }

  @action.bound
  async changeSort(newSorted: SortingRule[]) {
    this.sorted = newSorted;
    await this.fetchEmployees();
  }

  @action.bound
  async changeSize(size) {
    this.size = size;
    await this.fetchEmployees();
  }

  @action.bound
  async changePageCurrent(page) {
    this.page = page;
    await this.fetchEmployees();
  }

  @action.bound
  changeFilter(value) {
    this.page = DEFAULT_PAGE;
    this.searchTerm = value;
    this.onSearch();
  }
}

export default EmployeesStore;
